import * as React from 'react';
import { cn } from '@lib/cn';

export interface CustomCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  id?: string;
  className?: string;
  labelClassName?: string;
  disabled?: boolean;
  /** Size variants similar to Button: sm | md | lg */
  size?: 'sm' | 'md' | 'lg';
}

export function CustomCheckbox({
  checked,
  onChange,
  label,
  id,
  className,
  //   labelClassName,
  disabled = false,
  size = 'md',
}: CustomCheckboxProps) {
  const generatedId = React.useId();
  const checkboxId = id ?? generatedId;
  const wrapperSizes: Record<string, string> = {
    sm: ' gap-[--spacing-4]',
    md: ' gap-[--spacing-6]',
    lg: ' gap-[--spacing-8]',
  };
  const sizes: Record<string, string> = {
    sm: 'h-4 w-4 rounded-[--radi-2] border',
    md: 'h-[22px] w-[22px] rounded-[--radi-4] border',
    lg: 'h-[32px] w-[32px] rounded-[--radi-6] border-2',
  };

  const iconSizes: Record<string, string> = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-2xl',
  };
  const labelSizes: Record<string, string> = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg font-medium',
  };
  return (
    <label
      htmlFor={checkboxId}
      className={cn(
        wrapperSizes[size],
        'group cursor-pointer flex items-center select-none',
        disabled && 'cursor-not-allowed opacity-70',
        className,
      )}
    >
      {/* Invisible input */}
      <input
        id={checkboxId}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
        aria-label={typeof label === 'string' ? label : undefined}
        className="sr-only"
      />

      {/* Visible checkbox */}
      <span
        aria-hidden="true"
        className={cn(
          sizes[size],
          'flex items-center justify-center transition-all duration-200',
          checked
            ? 'border-[--state-brand-active] bg-[--state-brand-active]'
            : 'border-[--base-border] bg-[--base-fill-1] group-hover:border-[--state-brand-active]',
        )}
      >
        <i
          className={cn(
            'ri-check-line font-bold text-[--text-light-primary] transition-opacity duration-200',
            iconSizes[size],
            checked ? 'opacity-100' : 'opacity-0',
          )}
          aria-hidden="true"
        />
      </span>

      {label ? (
        <span className={cn(labelSizes[size], 'text-[--text-dark-primary]')}>{label}</span>
      ) : null}
    </label>
  );
}
